import { useEffect, useRef, useState } from 'react';
import { useCreateAsset, useUpdateAsset, useAssetCategories } from '../../../services/httpServices/queries';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { SearchableSelect } from '../../../components/ui/searchable-select';
import { DatePicker } from '../../../components/ui/DatePicker';
import { ValidatedNumberInput } from '../../../components/ui/validated-number-input';
import { cn } from '../../../lib/utils';
import {
  AlertCircle,
  Save,
  Package,
  X,
  CheckCircle2,
  Wrench,
  Trash2,
  Archive,
  Hash,
  Coins,
  CreditCard,
  Truck,
  MapPin,
  Layers,
  StickyNote,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { CreateAssetDto, FixedAssetStatus, FixedAsset } from '../../../types/asset';
import { PAYMENT_OPTIONS, PAYMENT_LABELS, fmtMoney, CURRENCY } from './assetLabels';

interface AssetFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Pass an asset to edit it, or leave undefined to add a new one. */
  asset?: FixedAsset | null;
}

const today = () => new Date().toISOString().slice(0, 10);

/** Visual treatment for each asset status, used by the status picker cards. */
const STATUS_CONFIG: Record<
  FixedAssetStatus,
  { label: string; icon: LucideIcon; color: string; bg: string; border: string }
> = {
  ACTIVE: {
    label: 'Active',
    icon: CheckCircle2,
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/40',
  },
  UNDER_MAINTENANCE: {
    label: 'Maintenance',
    icon: Wrench,
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/40',
  },
  DISPOSED: {
    label: 'Disposed',
    icon: Trash2,
    color: 'text-danger',
    bg: 'bg-danger/10',
    border: 'border-danger/40',
  },
  RETIRED: {
    label: 'Retired',
    icon: Archive,
    color: 'text-text-muted',
    bg: 'bg-text-muted/10',
    border: 'border-text-muted/40',
  },
};

/** Section heading with a coloured accent bar and an optional icon. */
function SectionHeading({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-1 h-4 bg-primary rounded-full" />
      {Icon && <Icon size={14} className="text-text-muted" />}
      <h3 className="text-xs font-semibold text-text uppercase tracking-wide">{children}</h3>
    </div>
  );
}

const fieldClass = (hasError?: boolean) =>
  cn(
    'w-full rounded-lg border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2',
    hasError ? 'border-danger focus:ring-danger/20' : 'border-border focus:ring-primary/20',
  );

export function AssetFormModal({ open, onClose, asset }: AssetFormModalProps) {
  const isEdit = Boolean(asset);

  const [name, setName] = useState('');
  const [assetCategoryId, setAssetCategoryId] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(today());
  const [quantity, setQuantity] = useState('1');
  const [unitCost, setUnitCost] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState<FixedAssetStatus>('ACTIVE');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const nameRef = useRef<HTMLInputElement>(null);

  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const { data: categoriesData, isLoading: categoriesLoading } = useAssetCategories(undefined, true, 'ASSET');
  const categories = categoriesData?.data || [];
  const isSubmitting = createAsset.isPending || updateAsset.isPending;

  useEffect(() => {
    if (!open) return;
    setName(asset?.name ?? '');
    setAssetCategoryId(asset?.assetCategoryId ?? asset?.assetCategory?.id ?? '');
    setPurchaseDate(asset?.purchaseDate ? asset.purchaseDate.slice(0, 10) : today());
    setQuantity(String(asset?.quantity ?? 1));
    setUnitCost(asset ? String(Number(asset.unitCost)) : '');
    setSupplierName(asset?.supplierName ?? asset?.vendor?.name ?? '');
    setLocation(asset?.location ?? '');
    setStatus(asset?.status ?? 'ACTIVE');
    setPaymentMethod(asset?.paymentMethod ?? 'cash');
    setDescription(asset?.description ?? '');
    setFormError(null);
    setTouched({});
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [open, asset]);

  // Close on Escape (unless a save is in flight).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  const qty = Math.max(1, Number(quantity) || 1);
  const unit = Number(unitCost) || 0;
  const categoryName = categories.find((c) => c.id === assetCategoryId)?.name;

  // Field-level validation surfaced once a field has been touched or on submit.
  const errors: Record<string, string> = {};
  if (!name.trim()) errors.name = 'Asset name is required';
  if (!unitCost.trim() || Number.isNaN(Number(unitCost)) || unit < 0) {
    errors.unitCost = 'Enter a valid unit cost';
  }

  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (Object.keys(errors).length > 0) {
      setTouched({ name: true, unitCost: true });
      return;
    }

    const payload: CreateAssetDto = {
      name: name.trim(),
      assetCategoryId: assetCategoryId || undefined,
      purchaseDate,
      quantity: qty,
      unitCost: unit,
      supplierName: supplierName.trim() || undefined,
      location: location.trim() || undefined,
      status,
      paymentMethod,
      description: description.trim() || undefined,
    };

    const onSuccess = () => onClose();
    const onError = (err: any) =>
      setFormError(
        err?.details?.map((d: any) => d.message).join(', ') ||
          err?.message ||
          'Failed to save asset',
      );

    if (isEdit && asset) {
      updateAsset.mutate({ id: asset.id, data: payload }, { onSuccess, onError });
    } else {
      createAsset.mutate(payload, { onSuccess, onError });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit asset' : 'Add asset'}
      onClick={() => !isSubmitting && onClose()}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-border bg-background/50 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/20 flex items-center justify-center">
              <Package size={22} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">
                {isEdit ? 'Edit Asset' : 'Add Asset'}
              </h2>
              <p className="text-xs text-text-muted truncate">
                {isEdit ? 'Update the details of this fixed asset' : 'Record a new asset the hospital owns'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="p-2 rounded-xl text-text-muted hover:text-text hover:bg-background transition-colors disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row lg:overflow-hidden overflow-y-auto">
            {/* Left: form */}
            <div className="flex-1 p-6 space-y-6 lg:overflow-y-auto">
              {formError && (
                <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              {/* Basic Information */}
              <div className="space-y-4">
                <SectionHeading icon={Layers}>Basic Information</SectionHeading>

                <Input
                  ref={nameRef}
                  label="Asset Name *"
                  placeholder="e.g. Digital X-Ray Machine"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => markTouched('name')}
                  maxLength={255}
                  error={touched.name ? errors.name : undefined}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-text">Category</label>
                    <SearchableSelect
                      value={assetCategoryId}
                      onChange={setAssetCategoryId}
                      loading={categoriesLoading}
                      placeholder="— Uncategorised —"
                      inputClassName="bg-surface"
                      options={categories.map((option) => ({
                        value: option.id,
                        label: option.name,
                        description: option.code,
                      }))}
                    />
                    {!categoriesLoading && categories.length === 0 && (
                      <p className="text-xs text-text-muted">
                        No categories yet — add one under Assets → Categories.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-text">Location</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. Radiology Department"
                        maxLength={255}
                        className={cn(fieldClass(), 'pl-9')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Details */}
              <div className="space-y-4">
                <SectionHeading icon={Wallet}>Purchase Details</SectionHeading>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DatePicker
                    label="Purchase Date *"
                    value={purchaseDate}
                    onChange={setPurchaseDate}
                  />

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-text">Payment Method</label>
                    <div className="relative">
                      <CreditCard size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className={cn(fieldClass(), 'pl-9 appearance-none')}
                      >
                        {PAYMENT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {paymentMethod === 'cash' && (
                      <p className="text-xs text-text-muted">
                        Cash purchases also record a cashbook cash-out entry.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-text">Quantity</label>
                    <div className="relative">
                      <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                      <input
                        type="number"
                        min={1}
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        className={cn(fieldClass(), 'pl-9')}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-text">Unit Cost ({CURRENCY}) *</label>
                    <div className="relative">
                      <Coins size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                      <ValidatedNumberInput
                        value={unitCost}
                        onChange={(v) => setUnitCost(String(v))}
                        onBlur={() => markTouched('unitCost')}
                        placeholder="0.00"
                        className={cn(
                          fieldClass(touched.unitCost && Boolean(errors.unitCost)),
                          'pl-9',
                        )}
                      />
                    </div>
                    {touched.unitCost && errors.unitCost && (
                      <p className="text-xs text-danger flex items-center gap-1">
                        <AlertCircle size={12} /> {errors.unitCost}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1 sm:col-span-2">
                    <label className="block text-sm font-medium text-text">Supplier</label>
                    <div className="relative">
                      <Truck size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
                      <input
                        type="text"
                        value={supplierName}
                        onChange={(e) => setSupplierName(e.target.value)}
                        placeholder="Optional — who supplied this asset"
                        maxLength={255}
                        className={cn(fieldClass(), 'pl-9')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-4">
                <SectionHeading icon={StickyNote}>Additional Notes</SectionHeading>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional notes about this asset"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-surface text-text text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>
            </div>

            {/* Right: summary rail */}
            <aside className="shrink-0 border-t lg:border-t-0 lg:border-l border-border bg-background/40 p-6 space-y-6 lg:w-80 lg:overflow-y-auto">
              {/* Status */}
              <div className="space-y-3">
                <SectionHeading icon={CheckCircle2}>Status</SectionHeading>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.keys(STATUS_CONFIG) as FixedAssetStatus[]).map((key) => {
                    const config = STATUS_CONFIG[key];
                    const Icon = config.icon;
                    const isSelected = status === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setStatus(key)}
                        className={cn(
                          'relative flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all',
                          isSelected
                            ? `${config.border} ${config.bg} ring-2 ring-primary/20`
                            : 'border-border bg-surface hover:bg-background',
                        )}
                      >
                        <Icon size={16} className={isSelected ? config.color : 'text-text-muted'} />
                        <span
                          className={cn(
                            'text-xs font-semibold leading-tight',
                            isSelected ? 'text-text' : 'text-text-muted',
                          )}
                        >
                          {config.label}
                        </span>
                        {isSelected && (
                          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Live total */}
              <div className="space-y-3">
                <SectionHeading icon={Wallet}>Total Cost</SectionHeading>
                <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-4">
                  <span className="block text-3xl font-bold text-primary tracking-tight">
                    {fmtMoney(qty * unit)}
                  </span>
                  <p className="text-xs text-text-muted mt-1">
                    {qty} × {fmtMoney(unit)}
                  </p>
                </div>
              </div>

              {/* Summary */}
              <div className="space-y-3">
                <SectionHeading icon={Layers}>Overview</SectionHeading>
                <dl className="space-y-2.5">
                  <SummaryRow icon={Layers} label="Category" value={categoryName || 'Uncategorised'} />
                  <SummaryRow icon={MapPin} label="Location" value={location.trim() || '—'} />
                  <SummaryRow icon={Truck} label="Supplier" value={supplierName.trim() || '—'} />
                  <SummaryRow
                    icon={CreditCard}
                    label="Payment"
                    value={PAYMENT_LABELS[paymentMethod] || paymentMethod}
                  />
                </dl>
              </div>
            </aside>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-background/50 shrink-0">
            <p className="hidden sm:block text-xs text-text-muted">
              Press <kbd className="px-1.5 py-0.5 rounded border border-border bg-surface font-sans">Esc</kbd> to cancel
            </p>
            <div className="flex items-center gap-3 ml-auto">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                {!isSubmitting && <Save size={16} className="mr-2" />}
                {isEdit ? 'Save Changes' : 'Add Asset'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Small labelled row used inside the right-hand overview rail. */
function SummaryRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 w-7 h-7 shrink-0 rounded-lg bg-surface border border-border flex items-center justify-center">
        <Icon size={13} className="text-text-muted" />
      </span>
      <div className="min-w-0">
        <dt className="text-[11px] uppercase tracking-wide text-text-muted">{label}</dt>
        <dd className="text-sm text-text truncate">{value}</dd>
      </div>
    </div>
  );
}
